import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchPlayerAuthCheck, getDefaultPlayerLoginUrl } from "@/lib/api";
import type { PlayerAuthCheckResponse } from "@/types/api";

export interface PlayerStudent {
  id: number;
  name: string;
}

export type PlayerAuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface PlayerAuthContextValue {
  status: PlayerAuthStatus;
  /** True when session is valid */
  isAuthenticated: boolean;
  student: PlayerStudent | null;
  /** From token exchange — deep-link to `/course/:id` */
  courseIdFromAuth: number | undefined;
  /** From auth-check when not logged in (or default) */
  loginUrl: string;
  /** Backend `error` e.g. `token_expired` */
  authError: string | undefined;
  /**
   * Set when the auth-check HTTP request failed (network, CORS, invalid JSON).
   * Distinct from `authenticated: false`, which means the API responded OK but no session.
   */
  authCheckFailure: string | undefined;
  /** True when admin opened Preview from the panel — don’t POST progress / exit-quiz */
  isAdminPreview: boolean;
  refreshAuth: () => Promise<void>;
}

const PlayerAuthContext = createContext<PlayerAuthContextValue | null>(null);

function applyAuthResponse(data: PlayerAuthCheckResponse): Omit<PlayerAuthContextValue, "refreshAuth"> {
  const loginUrl = data.login_url?.trim() || getDefaultPlayerLoginUrl();
  const authError = typeof data.error === "string" && data.error.trim() ? data.error.trim() : undefined;

  if (!data.authenticated) {
    return {
      status: "unauthenticated",
      isAuthenticated: false,
      student: null,
      courseIdFromAuth: undefined,
      loginUrl,
      authError,
      authCheckFailure: undefined,
      isAdminPreview: false,
    };
  }

  const sidRaw = data.student_id;
  const sid = typeof sidRaw === "number" ? sidRaw : Number(sidRaw);
  const id = Number.isFinite(sid) ? sid : 0;
  const name = (data.student_name ?? data.name ?? "").trim() || "Student";
  const cidRaw = data.course_id;
  const cid =
    cidRaw != null ? (typeof cidRaw === "number" ? cidRaw : Number(cidRaw)) : undefined;

  const isAdminPreview = data.is_admin === true;

  return {
    status: "authenticated",
    isAuthenticated: true,
    student: { id, name },
    courseIdFromAuth: Number.isFinite(cid as number) ? (cid as number) : undefined,
    loginUrl,
    authError: undefined,
    authCheckFailure: undefined,
    isAdminPreview,
  };
}

export function PlayerAuthProvider({
  children,
  initialUrlToken,
}: {
  children: ReactNode;
  initialUrlToken: string | null;
}) {
  const [value, setValue] = useState<Omit<PlayerAuthContextValue, "refreshAuth">>(() => ({
    status: "loading",
    isAuthenticated: false,
    student: null,
    courseIdFromAuth: undefined,
    loginUrl: getDefaultPlayerLoginUrl(),
    authError: undefined,
    authCheckFailure: undefined,
    isAdminPreview: false,
  }));

  const refreshAuth = useCallback(async () => {
    setValue((v) => ({ ...v, status: "loading", authCheckFailure: undefined }));
    try {
      const data = await fetchPlayerAuthCheck();
      const next = applyAuthResponse(data);
      setValue(next);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setValue({
        status: "unauthenticated",
        isAuthenticated: false,
        student: null,
        courseIdFromAuth: undefined,
        loginUrl: getDefaultPlayerLoginUrl(),
        authError: undefined,
        authCheckFailure: msg,
        isAdminPreview: false,
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPlayerAuthCheck(
          initialUrlToken ? { token: initialUrlToken } : undefined
        );
        if (cancelled) return;
        const next = applyAuthResponse(data);
        setValue(next);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setValue({
          status: "unauthenticated",
          isAuthenticated: false,
          student: null,
          courseIdFromAuth: undefined,
          loginUrl: getDefaultPlayerLoginUrl(),
          authError: undefined,
          authCheckFailure: msg,
          isAdminPreview: false,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialUrlToken]);

  const merged = useMemo<PlayerAuthContextValue>(
    () => ({
      ...value,
      refreshAuth,
    }),
    [value, refreshAuth]
  );

  return <PlayerAuthContext.Provider value={merged}>{children}</PlayerAuthContext.Provider>;
}

export function usePlayerAuth(): PlayerAuthContextValue {
  const ctx = useContext(PlayerAuthContext);
  if (!ctx) {
    throw new Error("usePlayerAuth must be used within PlayerAuthProvider");
  }
  return ctx;
}
